import { useTranslation } from 'react-i18next';

interface WorkbenchProgressProps {
  current: number;
  total: number;
  /** K5: Gesamtzahl gerade nicht ermittelbar (Zaehl-Query rot) — dann wird
   *  sie WEGGELASSEN statt mit der bloss geladenen Menge zu luegen. */
  totalUnknown?: boolean;
  sessionAccepted: number;
  sessionRejected: number;
}

export function WorkbenchProgress({
  current,
  total,
  totalUnknown = false,
  sessionAccepted,
  sessionRejected,
}: WorkbenchProgressProps) {
  const { t } = useTranslation('workbench');

  // Defensiv zusaetzlich zum Gate in WorkbenchReviewer: "Produkt 1 von 0" und
  // "3/0 geprueft" sind keine Fortschrittsanzeigen, sondern falsche Zahlen.
  if (total <= 0) return null;

  const reviewed = sessionAccepted + sessionRejected;
  // K5-Befund: die Sitzungszaehler leben im Scope Feed+Reiter und ueberleben
  // Filterwechsel per Design (Bekannt Nr. 29), total ist bei aktivem Filter
  // aber die GEFILTERTE Menge — 50 Entscheidungen auf total 24 ergaben einen
  // uebervollen Balken und "50/24 geprueft". Die Prozente sind geklemmt, und
  // ueberschreitet reviewed das total, faellt der Text auf die Fassung ohne
  // Grundmenge zurueck (die Zaehler selbst stimmen ja).
  const greenPct = Math.min(100, total > 0 ? (sessionAccepted / total) * 100 : 0);
  const redPct = Math.min(100 - greenPct, total > 0 ? (sessionRejected / total) * 100 : 0);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        {/* V3-Befund: die am haeufigsten wechselnde Zahl der Werkbank stand
            ohne tabular-nums — "Produkt 9 von 3.777" und "Produkt 10 von
            3.777" sind unterschiedlich breit, die Zeile zappelte bei jedem
            Blaettern. */}
        <span className="font-medium text-foreground tabular-nums">
          {totalUnknown
            ? t('progress.labelUnknown', { current: current + 1 })
            : t('progress.label', { current: current + 1, total })}
        </span>
        <span className="flex items-center gap-3 text-xs tabular-nums">
          {sessionAccepted > 0 && (
            <span className="flex items-center gap-1 text-success-ink">
              <span className="h-2 w-2 rounded-full bg-success" />
              {t('progress.accepted', { count: sessionAccepted })}
            </span>
          )}
          {sessionRejected > 0 && (
            <span className="flex items-center gap-1 text-destructive-ink">
              <span className="h-2 w-2 rounded-full bg-destructive" />
              {t('progress.rejected', { count: sessionRejected })}
            </span>
          )}
          {reviewed > 0 && (
            <span className="text-muted-foreground">
              {reviewed > total || totalUnknown
                ? t('progress.reviewedNoTotal', { count: reviewed })
                : t('progress.reviewed', { count: reviewed, total })}
            </span>
          )}
        </span>
      </div>

      {/* Multi-segment progress bar */}
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {greenPct > 0 && (
          <div
            className="h-full bg-success transition-all duration-300"
            style={{ width: `${greenPct}%` }}
          />
        )}
        {redPct > 0 && (
          <div
            className="h-full bg-destructive transition-all duration-300"
            style={{ width: `${redPct}%` }}
          />
        )}
      </div>
    </div>
  );
}
